import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, IconButton, Menu, MenuItem, TextField, FormControlLabel, Checkbox,
  Typography, Divider, Button, Select, FormControl, InputLabel, Grid
} from '@mui/material';
import { FilterAlt, Clear } from '@mui/icons-material';

const ColumnFilter = ({
  options = [],
  value,
  onChange,
  onClear,
  placeholder = "Filter...",
  type = "select",  // select, search, multiselect, searchMultiselect, numeric, date, text
  disabled = false,
  onDisabledClick
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pendingValue, setPendingValue] = useState(value);
  const [displayCount, setDisplayCount] = useState(100);
  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [numericFilter, setNumericFilter] = useState({ operator: 'equals', value: '' });
  const [dateFilter, setDateFilter] = useState({ operator: 'equals', value: '' });
  const [textFilter, setTextFilter] = useState({ operator: 'contains', value: '' });
  const open = Boolean(anchorEl);

  useEffect(() => { setPendingValue(value); }, [value]);

  useEffect(() => {
    if (type === 'multiselect' || type === 'searchMultiselect') {
      if (!value || (Array.isArray(value) && value.length === 0)) { setSearchTerm(''); setDebouncedSearchTerm(''); }
    } else if (type === 'numeric') {
      if (!value || value === null) setNumericFilter({ operator: 'equals', value: '' });
    } else if (type === 'date') {
      if (!value || value === null) setDateFilter({ operator: 'equals', value: '' });
    } else if (type === 'text') {
      if (!value || value === null) setTextFilter({ operator: 'contains', value: '' });
    } else if (type === 'search') {
      if (!value || value === '') { setSearchTerm(''); setDebouncedSearchTerm(''); }
    }
  }, [value, type]);

  useEffect(() => { setDisplayCount(100); }, [debouncedSearchTerm]);

  const handleClick = (event) => {
    if (disabled) { if (onDisabledClick) onDisabledClick(event); return; }
    if (type === 'date' && typeof value === 'object' && value !== null) setDateFilter({ operator: value.operator || 'equals', value: value.value || '' });
    if (type === 'text' && typeof value === 'object' && value !== null) setTextFilter({ operator: value.operator || 'contains', value: value.value || '' });
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => { setAnchorEl(null); setSearchTerm(''); setPendingValue(value); };
  const handleApply = () => { onChange(pendingValue); handleClose(); };
  const handleCancel = () => { setPendingValue(value); handleClose(); };

  const handleOptionClick = (optionValue) => {
    if (type === 'multiselect' || type === 'searchMultiselect') {
      const currentValue = Array.isArray(pendingValue) ? pendingValue : [];
      const newValue = currentValue.includes(optionValue)
        ? currentValue.filter(v => v !== optionValue)
        : [...currentValue, optionValue];
      setPendingValue(newValue);
    } else {
      onChange(optionValue);
      handleClose();
    }
  };

  const handleClear = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (type === 'multiselect' || type === 'searchMultiselect') {
      setPendingValue([]); setSearchTerm(''); onChange([]);
    } else if (type === 'numeric') {
      setNumericFilter({ operator: 'equals', value: '' }); onChange(null);
    } else if (type === 'date') {
      setDateFilter({ operator: 'equals', value: '' }); onChange(null);
    } else if (type === 'text') {
      setTextFilter({ operator: 'contains', value: '' }); onChange(null);
    } else if (type === 'search') {
      setSearchTerm(''); onChange('');
    } else {
      onChange('all');
    }
    if (onClear) await onClear();
    handleClose();
  };

  const handleSelectAll = (event) => {
    event.stopPropagation();
    if (type === 'multiselect' || type === 'searchMultiselect') {
      const filtered = options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()));
      setPendingValue(filtered.map(o => o.value));
    }
  };

  const handleNumericFilterApply = () => {
    if (numericFilter.operator === 'isNull' || numericFilter.operator === 'isNotNull') {
      onChange({ operator: numericFilter.operator, value: null });
    } else if (numericFilter.value !== '') {
      onChange({ operator: numericFilter.operator, value: parseFloat(numericFilter.value) });
    } else { onChange(null); }
    handleClose();
  };

  const handleDateFilterApply = () => {
    if (dateFilter.value !== '') onChange({ operator: dateFilter.operator, value: dateFilter.value });
    else onChange(null);
    handleClose();
  };

  const handleTextFilterApply = () => {
    if (textFilter.operator === 'isEmpty' || textFilter.operator === 'isNotEmpty') {
      onChange({ operator: textFilter.operator, value: null });
    } else if (textFilter.value !== '') {
      onChange({ operator: textFilter.operator, value: textFilter.value });
    } else { onChange(null); }
    handleClose();
  };

  const filteredOptions = useMemo(() => {
    return options.filter(o => o.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));
  }, [options, debouncedSearchTerm]);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && displayCount < filteredOptions.length) {
      setDisplayCount(prev => Math.min(prev + 100, filteredOptions.length));
    }
  }, [displayCount, filteredOptions.length]);

  const isActive = () => {
    if (type === 'multiselect' || type === 'searchMultiselect') return Array.isArray(value) && value.length > 0;
    if (type === 'numeric' || type === 'date') return value && typeof value === 'object' && value.value !== '';
    if (type === 'text') return value && typeof value === 'object' && (value.operator === 'isEmpty' || value.operator === 'isNotEmpty' || value.value !== '');
    if (type === 'search') return value && value !== '';
    return value && value !== 'all';
  };

  const actionButtons = (applyFn) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
      <Button size="small" onClick={handleClear}>Clear</Button>
      <Button size="small" variant="contained" onClick={applyFn}>Apply</Button>
    </Box>
  );

  const multiselectActions = (
    <>
      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {Array.isArray(pendingValue) ? pendingValue.length : 0} selected
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" onClick={handleSelectAll} sx={{ flex: '1 1 auto', minWidth: '70px' }}>Select All</Button>
          <Button size="small" onClick={handleClear} sx={{ flex: '1 1 auto', minWidth: '60px' }}>Clear</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={handleCancel} sx={{ flex: 1 }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleApply} sx={{ flex: 1 }}>Apply</Button>
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <IconButton
        size="small" onClick={handleClick}
        disabled={disabled && !onDisabledClick}
        aria-label={isActive() ? 'Clear filter' : 'Open filter'}
        sx={{
          color: isActive() ? 'primary.main' : (disabled ? 'text.disabled' : 'text.secondary'),
          backgroundColor: isActive() ? 'action.selected' : 'transparent',
          '&:hover': { backgroundColor: isActive() ? 'action.selected' : 'action.hover', transform: 'scale(1.1)' },
          padding: '6px', transition: 'all 0.2s ease',
          ...(disabled && onDisabledClick && { cursor: 'pointer', opacity: 0.7 })
        }}
      >
        <FilterAlt sx={{ fontSize: '18px' }} />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}
        PaperProps={{ sx: { minWidth: 200, maxWidth: 300, maxHeight: 400 } }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        {type === 'numeric' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Numeric Filter</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Operator</InputLabel>
                  <Select value={numericFilter.operator} onChange={(e) => setNumericFilter({...numericFilter, operator: e.target.value})} label="Operator">
                    <MenuItem value="equals">=</MenuItem>
                    <MenuItem value="greater">&gt;</MenuItem>
                    <MenuItem value="less">&lt;</MenuItem>
                    <MenuItem value="greaterEqual">&ge;</MenuItem>
                    <MenuItem value="lessEqual">&le;</MenuItem>
                    <MenuItem value="notEqual">&ne;</MenuItem>
                    <MenuItem value="isNull">Is Empty</MenuItem>
                    <MenuItem value="isNotNull">Is Not Empty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" type="number" placeholder="Value" value={numericFilter.value}
                  onChange={(e) => setNumericFilter({...numericFilter, value: e.target.value})}
                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleNumericFilterApply(); }}
                  fullWidth disabled={numericFilter.operator === 'isNull' || numericFilter.operator === 'isNotNull'} />
              </Grid>
            </Grid>
            {actionButtons(handleNumericFilterApply)}
          </Box>
        )}

        {type === 'search' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Search Filter</Typography>
            <TextField size="small" placeholder={placeholder} value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { onChange(searchTerm); handleClose(); } }}
              fullWidth autoFocus />
            {actionButtons(() => { onChange(searchTerm); handleClose(); })}
          </Box>
        )}

        {type === 'date' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Date Filter</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Operator</InputLabel>
                  <Select value={dateFilter.operator} onChange={(e) => setDateFilter({...dateFilter, operator: e.target.value})} label="Operator">
                    <MenuItem value="equals">On</MenuItem>
                    <MenuItem value="after">After</MenuItem>
                    <MenuItem value="before">Before</MenuItem>
                    <MenuItem value="afterEqual">On or After</MenuItem>
                    <MenuItem value="beforeEqual">On or Before</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" type="date" value={dateFilter.value}
                  onChange={(e) => setDateFilter({...dateFilter, value: e.target.value})}
                  fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            {actionButtons(handleDateFilterApply)}
          </Box>
        )}

        {type === 'text' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Text Filter</Typography>
            <Grid container spacing={2}>
              <Grid item xs={5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Operator</InputLabel>
                  <Select value={textFilter.operator} onChange={(e) => setTextFilter({...textFilter, operator: e.target.value})} label="Operator">
                    <MenuItem value="contains">Contains</MenuItem>
                    <MenuItem value="equals">Equals</MenuItem>
                    <MenuItem value="startsWith">Starts With</MenuItem>
                    <MenuItem value="endsWith">Ends With</MenuItem>
                    <MenuItem value="notContains">Not Contains</MenuItem>
                    <MenuItem value="isEmpty">Is Empty</MenuItem>
                    <MenuItem value="isNotEmpty">Is Not Empty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={7}>
                <TextField size="small" placeholder={placeholder || "Enter text..."} value={textFilter.value}
                  onChange={(e) => setTextFilter({...textFilter, value: e.target.value})}
                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleTextFilterApply(); }}
                  fullWidth autoFocus disabled={textFilter.operator === 'isEmpty' || textFilter.operator === 'isNotEmpty'} />
              </Grid>
            </Grid>
            {actionButtons(handleTextFilterApply)}
          </Box>
        )}

        {type === 'searchMultiselect' && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{placeholder}</Typography>
            <TextField size="small" placeholder={`Type to ${placeholder.toLowerCase()}...`} value={searchTerm}
              onChange={(e) => { e.stopPropagation(); setSearchTerm(e.target.value); }}
              onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
              fullWidth autoFocus sx={{ mb: 2 }} />
            <Divider sx={{ my: 1 }} />
            <Box ref={scrollRef} onScroll={handleScroll} sx={{ maxHeight: 250, overflow: 'auto' }}>
              {!debouncedSearchTerm && searchTerm ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>Searching...</Typography>
              ) : filteredOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No matches found</Typography>
              ) : (
                filteredOptions.slice(0, displayCount).map((option) => (
                  <MenuItem key={option.value} onClick={() => handleOptionClick(option.value)} sx={{ py: 0.5 }}>
                    <FormControlLabel
                      control={<Checkbox checked={Array.isArray(pendingValue) && pendingValue.includes(option.value)} size="small" />}
                      label={option.label} sx={{ margin: 0, width: '100%' }} />
                  </MenuItem>
                ))
              )}
            </Box>
            {multiselectActions}
          </Box>
        )}

        {(type === 'select' || type === 'multiselect') && [
          options.length > 10 && (
            <Box key="search" sx={{ p: 1 }}>
              <TextField size="small" placeholder={placeholder} value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} fullWidth onClick={(e) => e.stopPropagation()} />
            </Box>
          ),
          <MenuItem key="clear" onClick={handleClear}>
            <Clear fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">Clear Filter</Typography>
          </MenuItem>,
          <Divider key="divider" />
        ]}

        {(type === 'select' || type === 'multiselect') && [
          filteredOptions.map((option) => (
            <MenuItem key={option.value} onClick={() => handleOptionClick(option.value)}
              selected={type === 'multiselect' ? Array.isArray(pendingValue) && pendingValue.includes(option.value) : value === option.value}
              sx={option.isSpecial ? { backgroundColor: '#f5f5f5', borderBottom: '1px solid #E8E0D0', fontWeight: 500 } : {}}>
              {type === 'multiselect' ? (
                <FormControlLabel
                  control={<Checkbox checked={Array.isArray(pendingValue) && pendingValue.includes(option.value)} size="small" />}
                  label={option.label} sx={{ margin: 0 }} />
              ) : (
                <Typography variant="body2">{option.label}</Typography>
              )}
            </MenuItem>
          )),
          filteredOptions.length === 0 && searchTerm && (
            <MenuItem key="no-options" disabled><Typography variant="body2" color="text.secondary">No options found</Typography></MenuItem>
          )
        ]}

        {type === 'multiselect' && type !== 'searchMultiselect' && multiselectActions}
      </Menu>
    </Box>
  );
};

export default ColumnFilter;
